import { createReducer, createFeature, on } from '@ngrx/store';
import { Product } from '../../../objects/product';
import { DestinationActions, ProductActions } from '../actions';

interface QuantityPerProductId {
  [productId: number]: number;
}

interface InventoryCountPerDestinationAndProduct {
  [destinationId: number | string]: {
    [productId: number]: number | null;
  };
}

export interface State {
  product: Product | null;
  products: Product[] | null;

  productLoading: boolean;
  productInventoryLoading: boolean;
  productsLoading: boolean;

  quantityPerProduct: QuantityPerProductId;
  inventoryCountPerDestinationAndProduct: InventoryCountPerDestinationAndProduct;
  error: Error | null;
}

export const initialState: State = {
  product: null,
  products: null,

  productLoading: false,
  productInventoryLoading: false,
  productsLoading: false,

  quantityPerProduct: {},
  inventoryCountPerDestinationAndProduct: {},
  error: null,
};

export const productFeature = createFeature({
  name: 'product',
  reducer: createReducer(
    initialState,
    on(ProductActions.setProduct, (state, { product }) => ({ ...state, product })),
    on(ProductActions.setProductQuantity, (state, { quantity, product }) => ({
      ...state,
      quantityPerProduct: {
        ...state.quantityPerProduct,
        [product?.id ?? -1]: quantity,
      },
    })),

    on(ProductActions.loadProducts, state => ({ ...state, productsLoading: true, error: null })),
    on(ProductActions.loadProductsSuccess, (state, { products }) => ({ ...state, products, productsLoading: false, error: null })),
    on(ProductActions.loadProductsFailure, (state, { error }) => ({ ...state, product: null, productsLoading: false, error })),

    on(ProductActions.loadProduct, state => ({ ...state, productLoading: true, product: null, error: null })),
    on(ProductActions.loadProductSuccess, (state, { product }) => ({ ...state, product, productLoading: false, error: null })),
    on(ProductActions.loadProductFailure, (state, { error }) => ({ ...state, product: null, productLoading: false, error })),

    on(ProductActions.loadProductsByIds, state => ({ ...state, productsLoading: true, products: null, error: null })),
    on(ProductActions.loadProductsByIdsSuccess, (state, { products }) => ({
      ...state,
      products: [ ...new Set([...(state.products || []), ...(products || []) ])],
      productsLoading: false,
      error: null })),
    on(ProductActions.loadProductsByIdsFailure, (state, { error }) => ({ ...state, product: null, productsLoading: false, error })),

    on(ProductActions.loadProductInventoryCount, (state, { product, destination }) => {
      const destinationId = (destination?.destination_type === 'delivery' || (destination?.delivery_warehouse ?? false))
        ? 'delivery'
        : destination.id;

      return {
        ...state,
        inventoryCountPerDestinationAndProduct: {
          ...state.inventoryCountPerDestinationAndProduct,
          [destinationId]: {
            ...state.inventoryCountPerDestinationAndProduct[destinationId] || {},
            [product.id]: null,
          },
        },
        productInventoryLoading: true,
        error: null,
      };
    }),

    on(ProductActions.loadProductInventoryCountSuccess, (state, { product, destination, count }) => {
      const destinationId = (destination?.destination_type === 'delivery' || (destination?.delivery_warehouse ?? false))
        ? 'delivery'
        : destination.id;

      return {
        ...state,
        inventoryCountPerDestinationAndProduct: {
          ...state.inventoryCountPerDestinationAndProduct,
          [destinationId]: {
            ...state.inventoryCountPerDestinationAndProduct[destinationId] || {},
            [product.id]: count
            },
        },
        productInventoryLoading: false,
        error: null
      };
    }),
    on(ProductActions.loadProductInventoryCountFailure, (state, { error }) => ({
        ...state,
        productInventoryLoading: false,
        error,
      }
    )),

    // destination actions
    on(DestinationActions.setDestination, (state, { destination }) => ({
      ...state,
      products: state.products?.filter(product => product.destination_ids.includes(destination?.id as number)) ?? null,
    })),
  ),
});

export const {
  name: productFeatureKey,
  reducer,
  selectProductState,
  selectProduct,
  selectProducts,
  selectProductLoading,
  selectProductInventoryLoading,
  selectProductsLoading,
  selectQuantityPerProduct,
  selectInventoryCountPerDestinationAndProduct,
  selectError: selectProductError,
} = productFeature;
